import { URL_PREFIX } from '../constants/api';

export interface Label {
  label_id: number;
  name: string;
  family_path: string;
}

export interface MappedLabel {
  id: number;
  label_id: number;
  name: string;
  mapped_label: string;
  parent_path: string;
  updated_by: {
    email: string;
    id: number;
    name: string;
  };
}

export interface LabelMappingFlatten {
  label_id: number;
  name: string;
  parent_path: string;
  mapped_labels: MappedLabel[];
}

export async function getLabelMappings(): Promise<MappedLabel[]> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping`);

    if (!resp.ok) {
      throw new Error(`Failed to fetch label mappings: ${resp.statusText}`);
    }

    return (await resp.json()) as MappedLabel[];
  } catch (error) {
    console.error('getLabelMappings error:', error);

    throw error;
  }
}

export async function getLabelMappingFlatten(): Promise<LabelMappingFlatten[]> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping/flatten`);

    if (!resp.ok) {
      throw new Error(
        `Failed to fetch flattened label mappings: ${resp.statusText}`,
      );
    }

    const data = (await resp.json()) as LabelMappingFlatten[];

    return data.filter((d) => d.parent_path !== null && d.parent_path !== '');
  } catch (error) {
    console.error('getLabelMappingFlatten error:', error);

    throw error;
  }
}

export async function getLabels(): Promise<Label[]> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping/labels`);

    if (!resp.ok) {
      throw new Error(`Failed to fetch labels: ${resp.statusText}`);
    }

    return (await resp.json()) as Label[];
  } catch (error) {
    console.error('getLabels error:', error);

    throw error;
  }
}

export async function getUnmappedLabels(): Promise<string[]> {
  try {
    const resp = await fetch(
      `${URL_PREFIX}/api/v1/label-mapping/unmapped-labels`,
    );

    if (!resp.ok) {
      throw new Error(`Failed to fetch unmapped labels: ${resp.statusText}`);
    }

    return (await resp.json()) as string[];
  } catch (error) {
    console.error('getUnmappedLabels error:', error);

    throw error;
  }
}

export async function updateLabelMapping(
  labelId: number,
  mappedLabel: string,
): Promise<void> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping/${labelId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label_id: labelId,
        mapped_label: mappedLabel,
      }),
    });

    if (!resp.ok) {
      throw new Error(`Failed to update label mapping: ${resp.statusText}`);
    }
  } catch (error) {
    console.error('updateLabelMapping error:', error);

    throw error;
  }
}

export async function getMappedLabelsByLabelId(
  labelId: number,
): Promise<MappedLabel[]> {
  try {
    const resp = await fetch(
      `${URL_PREFIX}/api/v1/label-mapping/mappings/${labelId}`,
    );

    if (!resp.ok) {
      throw new Error(
        `Failed to fetch mapped labels by label ID: ${resp.statusText}`,
      );
    }

    return (await resp.json()) as MappedLabel[];
  } catch (error) {
    console.error('getMappedLabelsByLabelId error:', error);

    throw error;
  }
}

export async function createLabelMapping(
  labelId: number,
  mappedLabel: string,
  name: string,
): Promise<void> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label_id: labelId,
        name,
        mapped_label: mappedLabel,
      }),
    });

    if (!resp.ok) {
      throw new Error(`Failed to create label mapping: ${resp.statusText}`);
    }
  } catch (error) {
    console.error('createLabelMapping error:', error);

    throw error;
  }
}

export async function deleteLabelMapping(labelId: number): Promise<void> {
  try {
    const resp = await fetch(`${URL_PREFIX}/api/v1/label-mapping/${labelId}`, {
      method: 'DELETE',
    });

    if (!resp.ok) {
      throw new Error(`Failed to delete label mapping: ${resp.statusText}`);
    }
  } catch (error) {
    console.error('deleteLabelMapping error:', error);

    throw error;
  }
}
